// src/feature-module/mentor/shadowing/MentorShadowingTasksPage.tsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "../../../core/common/header/PageHeader";
import {
  FaHome,
  FaMicrophone,
  FaSpinner,
  FaUsers,
  FaEye,
  FaClipboardList,
  FaSync,
} from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type ShadowingTask = {
  id: number;
  level: string;
  taskNumber: number;
  taskDescription: string;
  audioDownloadUrl: string;
  audioFilename: string;
  isActive: boolean;
};

const MentorShadowingTasksPage: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<ShadowingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/shadowing-tasks`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setTasks(data);
        setLastUpdated(new Date());
      } else {
        setError("Failed to load shadowing tasks");
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchTasks();
    setRefreshing(false);
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return "No description";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Group tasks by level
  const tasksByLevel = tasks.reduce<Record<string, ShadowingTask[]>>(
    (acc, task) => {
      const level = task.level || "Unspecified";
      if (!acc[level]) acc[level] = [];
      acc[level].push(task);
      return acc;
    },
    {}
  );

  const levelOrder = ["A1", "A2", "B1", "B2", "C1", "C2", "Unspecified"];
  const sortedLevels = Object.keys(tasksByLevel).sort((a, b) => {
    const indexA = levelOrder.indexOf(a);
    const indexB = levelOrder.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  const renderTaskTable = (level: string, taskList: ShadowingTask[]) => {
    const sortedTasks = [...taskList].sort(
      (a, b) => a.taskNumber - b.taskNumber
    );

    return (
      <div className="mb-10" key={level}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold text-[#009fe3] flex items-center gap-2">
            <FaMicrophone />
            Level: {level}
          </h3>
          <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-md">
            {sortedTasks.length} / 14 tasks
          </span>
        </div>
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Task #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Audio
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {sortedTasks.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="py-10 text-center text-gray-400 font-semibold"
                    >
                      No shadowing tasks found for this level.
                    </td>
                  </tr>
                ) : (
                  sortedTasks.map((task, index) => (
                    <tr
                      key={task.id}
                      className={`${
                        index % 2 === 1 ? "bg-gray-50" : "bg-white"
                      } hover:bg-blue-50 transition-colors`}
                    >
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-800 font-bold rounded-md">
                          {task.taskNumber}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                          {task.level}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-700 text-sm">
                          {truncateText(task.taskDescription, 120)}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center">
                          {task.audioDownloadUrl ? (
                            <audio
                              controls
                              className="w-48"
                              src={task.audioDownloadUrl}
                            >
                              Your browser does not support audio playback.
                            </audio>
                          ) : (
                            <span className="text-gray-400 text-sm">
                              No audio
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() =>
                              navigate(
                                `/mentor/shadowing-tasks/${task.id}/assign`
                              )
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition-colors text-sm font-semibold"
                            title="Assign to Batches"
                          >
                            <FaUsers size={14} />
                            Assign
                          </button>
                          <button
                            onClick={() =>
                              navigate(
                                `/mentor/shadowing-tasks/${task.id}/submissions`
                              )
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-semibold"
                            title="View Submissions"
                          >
                            <FaEye size={14} />
                            Review
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaMicrophone />}
        title="Shadowing Tasks"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/mentor/dashboard" },
          { label: "Shadowing Tasks" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Info Banner & Actions */}
        <div className="mb-6 bg-white rounded-lg shadow-lg p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex-1">
              <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-3">
                📋 <strong>Note:</strong> You can assign tasks to your batches
                and review student submissions.
              </p>
            </div>
            <button
              onClick={() => navigate("/mentor/shadowing-tasks/batch-summary")}
              className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap"
            >
              <FaClipboardList />
              View Batch Summary
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md">
            <p className="font-semibold">Error</p>
            <p>{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center text-[#009fe3] font-semibold py-20 text-xl flex items-center justify-center gap-2">
            <FaSpinner className="animate-spin" />
            Loading shadowing tasks...
          </div>
        ) : (
          <>
            {sortedLevels.length === 0 ? (
              <div className="text-center text-gray-400 py-10 bg-white rounded-lg shadow-lg">
                <FaMicrophone className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  No Shadowing Tasks Available
                </h3>
                <p className="text-gray-500">
                  No shadowing tasks have been created yet. Please contact an
                  administrator.
                </p>
              </div>
            ) : (
              sortedLevels.map((level) =>
                renderTaskTable(level, tasksByLevel[level])
              )
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default MentorShadowingTasksPage;
